/**
 * Tier 0 — magic-byte container sniffing.
 *
 * Reads only the first bytes of the file (via `File.slice`, so it never loads a
 * multi-GB file into memory) and verifies it is a real ISO-BMFF / QuickTime
 * container. This catches spoofed files — a `.txt`/`.exe`/`.jpg` renamed to
 * `.mp4` has no `ftyp` box and is rejected. It does NOT decode the video, so it
 * works for every codec (H.264, HEVC, ProRes, …).
 */

/** Bytes to read: enough for the `ftyp` box header + brand fields. */
const HEADER_BYTES = 32;

/** EBML header magic — every WebM/Matroska file starts with these four bytes. */
const EBML_MAGIC = [0x1a, 0x45, 0xdf, 0xa3];

export type VideoContainer = 'mp4' | 'quicktime' | 'webm';

const asciiAt = (view: DataView, offset: number, length: number): string => {
  let out = '';
  for (let i = 0; i < length; i += 1) {
    out += String.fromCharCode(view.getUint8(offset + i));
  }
  return out;
};

/** QuickTime files use the `qt  ` major brand; everything else we treat as MP4. */
const isQuickTimeBrand = (brand: string): boolean => brand === 'qt  ';

/**
 * Pure header parser — classifies an already-read header buffer. Returns the
 * container family for a valid MP4/QuickTime file, otherwise `null`. Split out
 * from the file read so the parsing logic can be unit-tested without binary
 * File I/O (which jsdom does not reliably support).
 */
export function parseVideoContainer(
  header: ArrayBuffer,
): VideoContainer | null {
  if (header.byteLength < 12) return null;

  const view = new DataView(header);

  if (EBML_MAGIC.every((byte, i) => view.getUint8(i) === byte)) return 'webm';

  // ISO base media files start with a File Type Box: [uint32 size]['ftyp'].
  const boxSize = view.getUint32(0);
  const boxType = asciiAt(view, 4, 4);
  if (boxType !== 'ftyp') return null;

  // Sanity-check the declared box size to reject coincidental 'ftyp' bytes.
  if (boxSize < 8 || boxSize > header.byteLength * 64) return null;

  const majorBrand = asciiAt(view, 8, 4);
  return isQuickTimeBrand(majorBrand) ? 'quicktime' : 'mp4';
}

/**
 * Reads the first bytes of a file and classifies its container. Throws only if
 * the bytes can't be read (caller treats a throw as `unreadable` → fail-closed).
 */
export async function sniffVideoContainer(
  file: File | Blob,
): Promise<VideoContainer | null> {
  const buf = await file.slice(0, HEADER_BYTES).arrayBuffer();
  return parseVideoContainer(buf);
}
